import * as React from 'react';
import type { InnerGlowRenderSettings } from '../utils/getTextInnerGlow';
import { TextEffectBlend } from './textEffectBlend';
import { effectContourTable } from '../utils/textEffectContours';
import { textEffectFilterRegion } from './textEffectFilterRegion';
import { innerGlowFootprint } from './innerGlowFootprint';

type Props = {
  readonly id: string;
  readonly glow: InnerGlowRenderSettings;
  readonly width: number;
  readonly height: number;
};

// Color Dodge attenuates its source color before blending. Other modes blend
// the authored color first, then interpolate at the inward mask's opacity.
// Final atop preserves the incoming glyph alpha, including antialiased counters.
// Range scales the falloff response; these are provisional rendering semantics.
const currentPixelRatio = () =>
  typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;

export function TextInnerGlowFilter({ id, glow, width, height }: Props) {
  const padding = glow.size + 2;
  const isColorDodge = glow.blendMode === 'color-dodge';
  const isEdge = glow.source === 'edge';
  const isLinear = glow.contour === 'linear';
  // Convolution taps are physical filter pixels; other lengths remain CSS units.
  const [pixelRatio, setPixelRatio] = React.useState(currentPixelRatio);
  React.useEffect(() => {
    const update = () => setPixelRatio(currentPixelRatio());
    const resolution = window.matchMedia?.(`(resolution: ${pixelRatio}dppx)`);
    resolution?.addEventListener?.('change', update);
    window.addEventListener('resize', update);
    return () => {
      resolution?.removeEventListener?.('change', update);
      window.removeEventListener('resize', update);
    };
  }, [pixelRatio]);
  // Skia accepts at most 256 convolution entries; the largest odd kernel is 255.
  // Larger scaled glows retain the existing Gaussian falloff instead of dropping out.
  const footprint = React.useMemo(
    () =>
      isColorDodge &&
      isEdge &&
      isLinear &&
      glow.erosion === 0 &&
      glow.footprintSize * pixelRatio <= 128
        ? innerGlowFootprint(glow.footprintSize * pixelRatio)
        : null,
    [
      isColorDodge,
      isEdge,
      isLinear,
      glow.erosion,
      glow.footprintSize,
      pixelRatio,
    ]
  );
  const [red, green, blue] = [1, 3, 5].map(
    (offset) => parseInt(glow.color.slice(offset, offset + 2), 16) / 255
  );
  return (
    <filter
      id={id}
      {...textEffectFilterRegion(width, height, padding)}
      colorInterpolationFilters="sRGB"
    >
      <feMorphology
        in="SourceAlpha"
        operator="erode"
        radius={glow.erosion}
        result="glowInset"
      />
      {footprint ? (
        <>
          <feConvolveMatrix
            in="glowInset"
            order={`${footprint.order} 1`}
            kernelMatrix={footprint.kernel}
            divisor={1}
            edgeMode="none"
            preserveAlpha={false}
            result="glowHorizontal"
          />
          <feConvolveMatrix
            in="glowHorizontal"
            order={`1 ${footprint.order}`}
            kernelMatrix={footprint.kernel}
            divisor={1}
            edgeMode="none"
            preserveAlpha={false}
            result="glowBlur"
          />
        </>
      ) : (
        <feGaussianBlur
          in="glowInset"
          stdDeviation={glow.blur}
          result="glowBlur"
        />
      )}
      <feComponentTransfer in="glowBlur" result="glowFalloff">
        <feFuncA
          type="linear"
          slope={isEdge ? -glow.rangeScale : glow.rangeScale}
          intercept={isEdge ? glow.rangeScale : 0}
        />
      </feComponentTransfer>
      {!isLinear && (
        <feComponentTransfer in="glowFalloff" result="glowContoured">
          <feFuncA
            type="table"
            tableValues={effectContourTable(glow.contour)}
          />
        </feComponentTransfer>
      )}
      <feComponentTransfer
        in={isLinear ? 'glowFalloff' : 'glowContoured'}
        result="glowMask"
      >
        <feFuncA type="linear" slope={glow.opacity} />
      </feComponentTransfer>
      {/* Blend unassociated RGB for every mode, then restore glyph coverage.
          Blending against translucent SourceGraphic changes the paint color. */}
      <feComponentTransfer in="SourceGraphic" result="glowBase">
        <feFuncA type="table" tableValues="1 1" />
      </feComponentTransfer>
      {isColorDodge ? (
        <>
          {/* Black is neutral for Dodge. Incorporate falloff and opacity into
              RGB, so a bright fill saturates sooner than a dark fill. */}
          <feColorMatrix
            in="glowMask"
            type="matrix"
            values={`0 0 0 ${red} 0 0 0 0 ${green} 0 0 0 0 ${blue} 0 0 0 0 0 1`}
            result="glowColor"
          />
          <TextEffectBlend
            foreground="glowColor"
            background="glowBase"
            mode="color-dodge"
            result="glowPaint"
          />
        </>
      ) : (
        <>
          <feFlood floodColor={glow.color} result="glowColor" />
          <TextEffectBlend
            foreground="glowColor"
            background="glowBase"
            mode={glow.blendMode}
            result="glowBlend"
          />
          <feComposite
            in="glowBlend"
            in2="glowMask"
            operator="in"
            result="glowPaint"
          />
        </>
      )}
      <feComposite in="glowPaint" in2="SourceGraphic" operator="atop" />
    </filter>
  );
}
